/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { getFirestore, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

interface UserData {
  userId: string;
  email: string;
  firstname: string;
  lastname: string;
  collectionHandles: string[];
  //emailVerified: boolean;
  isAdmin: boolean;
  lastSignInAt?: Timestamp | any;
  lastLoginAt?: string;
  loginCount?: number;
}

interface AdminUserLookupProps {
  userIsAdmin: boolean;
}

export default function AdminUserLookup({ userIsAdmin }: AdminUserLookupProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setIsLoading(true);
    setUserData(null);
    setError(null);

    try {
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(`No user found with email: ${email}`);
        setIsLoading(false);
        return;
      }

      // Get the first matching user
      const userDoc = querySnapshot.docs[0];
      const data = userDoc.data();

      setUserData({
        userId: userDoc.id,
        email: data.email || '',
        firstname: data.firstname || '',
        lastname: data.lastname || '',
        collectionHandles: data.collectionHandles || data.tags || [],
      //  emailVerified: data.emailVerified || false,
        isAdmin: data.isAdmin || false,
        lastSignInAt: data.lastSignInAt || undefined,
        lastLoginAt: data.lastLoginAt || undefined,
        loginCount: data.loginCount || 0,
      });
    } catch (err) {
      console.error('Error looking up user:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setUserData(null);
    setError(null);
  };

  const formatLastLogin = (lastSignInAt?: Timestamp | any, lastLoginAt?: string) => {
    // Prefer lastSignInAt (Firestore timestamp from Firebase function)
    let date: Date | null = null;
    
    if (lastSignInAt) {
      if (lastSignInAt.toDate) {
        // Firestore Timestamp
        date = lastSignInAt.toDate();
      } else if (lastSignInAt.seconds) {
        // Timestamp object
        date = new Date(lastSignInAt.seconds * 1000);
      }
    } else if (lastLoginAt) {
      // Fallback to lastLoginAt (ISO string)
      date = new Date(lastLoginAt);
    }
    
    if (!date) return 'Never';
    
    try {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  if (!userIsAdmin) {
    return null;
  }

  return (
    <div id="admin-user-lookup" >
      <h3 >User Lookup</h3>
      <p >
        Enter a user's email address to view their account details.
      </p>

      <form onSubmit={handleLookup} className="space-y-4">
        <div>
             <label>
          User Email:
           </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
         
        </div>

        <div className="button-row">
          <button
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Looking up...' : 'Lookup User'}
          </button>
          
          {(userData || error) && (
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {isLoading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-blue-800">🔍 Searching for user...</p>
        </div>
      )}

      {userData && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
          <p className="text-green-800 font-semibold mb-3">✅ User Found</p>
          
          <div className="space-y-2 text-sm">
            <div className="line-item">
              <span className="bold">First Name:</span>
              <span className="property-value">{userData.firstname || '(not set)'}</span>
            </div>
            
            <div className="line-item">
              <span className="bold">Last Name:</span>
              <span className="property-value">{userData.lastname || '(not set)'}</span>
            </div>
            
            <div className="line-item">
              <span className="bold">Email:</span>
              <span className="property-value">{userData.email}</span>
            </div>
            
            <div className="line-item">
              <span className="bold">User ID:</span>
              <span className="property-value font-mono text-xs">{userData.userId}</span>
            </div>
            
            {/* <div className="line-item">
              <span className="bold">Email Verified:</span>
              <span className={userData.emailVerified ? 'text-green-700' : 'text-red-700'}>
                {userData.emailVerified ? '✓ Yes' : '✗ No'}
              </span>
            </div> */}
            
            <div className="line-item">
              <span className="bold">Admin Status:</span>
              <span className={userData.isAdmin ? 'text-green-700 font-semibold' : 'text-gray-600'}>
                {userData.isAdmin ? 'Admin' : 'Regular User'}
              </span>
            </div>
            
            <div className="line-item">
              <span className="bold">Last Sign In:</span>
              <span className="property-value">
                {formatLastLogin(userData.lastSignInAt, userData.lastLoginAt)}
              </span>
            </div>
            
            <div className="line-item">
              <span className="bold">Login Count:</span>
              <span className="property-value">
                {userData.loginCount} 
              </span>
            </div>
            
            <div className="line-item">
              <span className="bold">Collections:</span>
              <div className="property-value">
                {userData.collectionHandles.length > 0 ? (
                  <ul>
                    {userData.collectionHandles.map((handle, index) => (
                      <li key={index} className="text-sm">{handle}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-gray-500 italic">(no collections assigned)</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-800 font-semibold">❌ Error</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}
    </div>
  );
}